import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { PageLevelTableWrapper } from '@base'
import { Table as TableV2, useTableReducer } from '@baseV2'
import { DispatchPlanFilters } from '@constants'
import { getAllDispatchPlanCards } from '@data'
import { paths } from '@routes'
import { LightTheme, Spacings, Text } from '@styles'

import { getDispatchPlanCardColumns } from '../dispatchPlanCardColumns'

const { background } = LightTheme.palette

// URL key of the pill, which useTableReducer sends to the api as dispatchStatusFilter
const DISPATCH_STATUS_PARAM = 'dispatch_status'

const DISPATCH_STATUS_PILLS = [
	{ value: 'ACTIVE', label: 'Active', color: background.dark },
	{ value: 'VEHICLES_NOT_PLANNED', label: 'Vehicles Not Planned', color: background.redDark },
	{ value: 'PARTIAL_VEHICLES_PLANNED', label: 'Partial Vehicles Planned', color: background.yellow },
	{ value: 'ALL_VEHICLES_PLANNED', label: 'All Vehicles Planned', color: background.blue },
	{ value: 'UPLOAD_DOCS_FOR_INVOICING', label: 'Upload Docs for Invoicing', color: background.pastelRed },
	{ value: 'READY_FOR_INVOICING', label: 'Ready For Invoicing', color: background.green },
	{ value: 'INVOICED', label: 'Invoiced', color: background.orangeLight },
	{ value: 'COMPLETED', label: 'Completed', color: background.greenDark },
]

const FilterBar = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
	padding: 0 0 ${Spacings.SPACING_3B} 0;
`

const StatusDot = styled.span`
	display: inline-block;
	width: ${Spacings.SPACING_2B};
	height: ${Spacings.SPACING_2B};
	border-radius: 50%;
	background: ${({ color }) => color};
	flex-shrink: 0;
`

const FilterChip = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6};
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
	border-radius: ${Spacings.SPACING_5B};
	border: 1px solid ${({ theme, $active }) => ($active ? 'transparent' : theme.palette.background.tablecolumn)};
	background: ${({ theme, $active, $color }) => ($active ? $color : theme.palette.background.white)};
	color: ${({ theme, $active }) => ($active ? theme.palette.text.white : 'inherit')};
	font-weight: ${({ $active }) => ($active ? Text.BOLD_600 : Text.BOLD_400)};
	cursor: pointer;
	font-size: ${Text.MEDIUM};
	white-space: nowrap;
	user-select: none;
	transition: background 0.15s;

	&:hover {
		background: ${({ theme, $active, $color }) => ($active ? $color : theme.palette.background.tablegrey)};
	}
`

const AllDispatchPlans = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const activePill =
		DISPATCH_STATUS_PILLS.find((pill) => pill.value && pill.value === searchParams.get(DISPATCH_STATUS_PARAM)) ?? DISPATCH_STATUS_PILLS[0]

	// Landing here without a pill in the URL still shows Active, so write that default into the URL —
	// the page then matches what it is showing, and a refresh or a shared link keeps the same list.
	useEffect(() => {
		if (searchParams.get(DISPATCH_STATUS_PARAM)) return
		const existing = Object.fromEntries(searchParams.entries())
		setSearchParams({ ...existing, page: existing.page ?? 0, [DISPATCH_STATUS_PARAM]: DISPATCH_STATUS_PILLS[0].value }, { replace: true })
	}, [searchParams, setSearchParams])

	const pillClickHandler = (pill) => {
		const existing = Object.fromEntries(searchParams.entries())
		delete existing[DISPATCH_STATUS_PARAM]
		setSearchParams({ ...existing, ...(pill.value ? { [DISPATCH_STATUS_PARAM]: pill.value } : {}), page: 0 })
	}

	const navigate = useNavigate()
	const [state, dispatch, actions] = useTableReducer(DispatchPlanFilters)
	const { forceUpdate, update } = useOutletContext()
	const userId = useSelector((state) => state.getIn(['user', 'user', 'user_id']))

	// The pill is the source of truth for vehicleStageFilter: on the first load the URL has no
	// dispatch_status yet, and the list still has to be the Active one.
	const filters = useMemo(
		() => ({ ...state.query.filters, [DispatchPlanFilters[DISPATCH_STATUS_PARAM]]: activePill.value }),
		[state.query.filters, activePill.value]
	)

	// useTableReducer hands back a fresh query object when it syncs with the URL, so the same fetch
	// would otherwise run twice on load.
	const lastFetchedKey = useRef()

	useEffect(() => {
		if (state.loading) {
			return
		}
		const fetchKey = JSON.stringify({ page: state.query.page, filters, forceUpdate })
		if (lastFetchedKey.current === fetchKey) return
		lastFetchedKey.current = fetchKey

		dispatch(actions.fetchTableDataProgress())
		getAllDispatchPlanCards(userId, state.query.page, 10, filters)
			.then((response) => {
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((error) => dispatch(actions.fetchTableDataFailure(error)))
	}, [state.query, filters, forceUpdate])

	const columns = useMemo(() => getDispatchPlanCardColumns(), [])

	const rowClickHandler = (row) => {
		navigate(paths.admin_manage_enquiry_order_edit_dispatch_plans_edit(row?.order_id, row?.dispatch_plan_id))
	}

	const onPageChangeHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}

	return (
		<PageLevelTableWrapper>
			<FilterBar>
				{DISPATCH_STATUS_PILLS.map((pill) => (
					<FilterChip key={pill.value ?? 'all'} $active={activePill === pill} $color={pill.color} onClick={() => pillClickHandler(pill)}>
						{pill.value && <StatusDot color={pill.color} />}
						{pill.label}
					</FilterChip>
				))}
			</FilterBar>
			<TableV2
				data={state.data}
				columns={columns}
				loading={state.loading}
				rowsToDisplay={10}
				page={state.query.page}
				totalItemCount={state.total_count}
				onPageChange={onPageChangeHandler}
				onRowClick={rowClickHandler}
				fullHeight
			/>
		</PageLevelTableWrapper>
	)
}

export default AllDispatchPlans
